import { Paperclip, CheckCircle2 } from "lucide-react";

/**
 * v77.4 — Single source of truth for the receipt status pill that
 * appears on every collapsed expense card (and inside the detail panel
 * for compact contexts). Three states, derived from the expense doc:
 *
 *   • attached      → 1+ files in `receipt_file_ids`
 *   • not_required  → user explicitly toggled `receipt_not_required`
 *   • missing       → none attached and not marked as not-required
 *
 * Action callbacks are optional; when omitted the pill renders as
 * read-only (used for read-only contexts like history or audit views).
 *
 * Props:
 *   • status        — derived label ("attached" | "missing" | "not_required")
 *   • count         — number of receipts (only used when status==="attached")
 *   • testid        — base data-testid (suffixed `-pill`, `-add`, `-view`, `-mark-not-needed`, `-undo-not-needed`)
 *   • onAdd         — click handler for "Add" (creator only; missing state)
 *   • onView        — click handler for "View" (attached state, optional)
 *   • onMarkNotNeeded — click handler for "Mark as not needed" (creator only; missing state)
 *   • onUndoNotNeeded — click handler for "Undo" (creator only; not_required state)
 *   • compact       — when true (default), renders as a single inline pill
 */
export function ReceiptStatusBadge({
  status,
  count = 0,
  testid = "receipt-badge",
  onAdd,
  onView,
  onMarkNotNeeded,
  onUndoNotNeeded,
  compact = true,
}) {
  const palette = {
    attached: {
      border: "border-[#1F9FA4]",
      bg: "bg-[#D6F0EF]",
      text: "text-[#116B6E]",
      action: "text-[#116B6E] hover:text-[#0B4D50]",
    },
    not_required: {
      border: "border-[#A6BFB6]",
      bg: "bg-[#E8F1ED]",
      text: "text-[#2E5044]",
      action: "text-[#2E5044] hover:text-[#1F3B30]",
    },
    missing: {
      border: "border-[#D9A441]",
      bg: "bg-[#F7EDD4]",
      text: "text-[#7A5A1E]",
      action: "text-[#7A5A1E] hover:text-[#5A4218]",
    },
  }[status] || {};

  const Icon = status === "not_required" ? CheckCircle2 : Paperclip;
  let label;
  if (status === "attached") {
    label = count > 1 ? `${count} receipts attached` : "Receipt attached";
  } else if (status === "not_required") {
    label = "No receipt needed";
  } else {
    label = "No receipt added";
  }

  return (
    <div
      className={`mt-1.5 inline-flex items-center gap-1.5 text-[11px] ${palette.bg} ${palette.text} border ${palette.border} px-2 py-0.5 rounded-sm`}
      data-testid={`${testid}-pill`}
    >
      <Icon className="w-3 h-3 shrink-0" />
      <span className="font-medium">{label}</span>

      {/* Action affordances (only when handlers provided). All actions
          stop propagation so wrapping the pill in a clickable card —
          e.g. an expanding row — won't toggle the row when the user
          taps the action. */}
      {status === "attached" && onView && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onView(e); }}
          className={`ml-1 text-[10px] uppercase tracking-[0.08em] ${palette.action} underline-offset-2 hover:underline`}
          data-testid={`${testid}-view`}
        >View</button>
      )}
      {status === "missing" && onAdd && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onAdd(e); }}
          className={`ml-1 text-[10px] uppercase tracking-[0.08em] ${palette.action} underline-offset-2 hover:underline`}
          data-testid={`${testid}-add`}
        >Add</button>
      )}
      {status === "missing" && onMarkNotNeeded && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onMarkNotNeeded(e); }}
          className={`text-[10px] tracking-[0.04em] ${palette.action} underline-offset-2 hover:underline`}
          data-testid={`${testid}-mark-not-needed`}
        >Not needed</button>
      )}
      {status === "not_required" && onUndoNotNeeded && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onUndoNotNeeded(e); }}
          className={`ml-1 text-[10px] uppercase tracking-[0.08em] ${palette.action} underline-offset-2 hover:underline`}
          data-testid={`${testid}-undo-not-needed`}
        >Undo</button>
      )}
    </div>
  );
}

/**
 * v77.4 — Pure helper used by `Expenses.jsx` (collapsed card +
 * dashboard tile + filter). Single source of truth — keep this in lock-
 * step with the backend's `_expense_receipt_status`.
 */
export function deriveReceiptStatus(expense) {
  const ids = [];
  const seen = new Set();
  if (expense?.receipt_file_id && !seen.has(expense.receipt_file_id)) {
    ids.push(expense.receipt_file_id); seen.add(expense.receipt_file_id);
  }
  for (const rid of (expense?.receipt_file_ids || [])) {
    if (rid && !seen.has(rid)) { ids.push(rid); seen.add(rid); }
  }
  if (ids.length > 0) return { status: "attached", count: ids.length, ids };
  if (expense?.receipt_not_required) return { status: "not_required", count: 0, ids: [] };
  return { status: "missing", count: 0, ids: [] };
}
